/** Keys whose selected values are category UUIDs for `categoryFilters`, not `history_row_filters`. */
export const CASE_FILES_CATEGORY_FILTER_KEYS = ['Category', 'Sub-Category'] as const;

/** Left-rail order in the case-files filter modal (after Date range when shown). */
export const CASE_FILES_FILTER_MODAL_CATEGORY_ORDER = [
  'Handler',
  'Corres. to',
  'Correspondence type',
  'Proclaim Folder',
  ...CASE_FILES_CATEGORY_FILTER_KEYS,
] as const;

export function compareCaseFilesFilterModalCategoryOrder(a: string, b: string): number {
  const order = CASE_FILES_FILTER_MODAL_CATEGORY_ORDER;
  const indexA = (order as readonly string[]).indexOf(a);
  const indexB = (order as readonly string[]).indexOf(b);
  const rankA = indexA === -1 ? order.length : indexA;
  const rankB = indexB === -1 ? order.length : indexB;
  if (rankA !== rankB) return rankA - rankB;
  if (indexA === -1 && indexB === -1) return a.localeCompare(b);
  return 0;
}

export function isCaseFilesCategoryFilterKey(key: string): boolean {
  return (CASE_FILES_CATEGORY_FILTER_KEYS as readonly string[]).includes(key);
}

/** `categoryFilterOptions` from `/matter/filter-options`: each row is `[label, id]` (UUID). */
export type CaseFilesCategoryFilterOptionsFromApi = Record<string, string[][]>;

export type CaseFilesCategoryFilterOption = { label: string; id: string };

export function normalizeCaseFilesCategoryFilterOptions(
  raw: CaseFilesCategoryFilterOptionsFromApi | undefined,
): Record<string, CaseFilesCategoryFilterOption[]> {
  if (!raw) return {};
  const out: Record<string, CaseFilesCategoryFilterOption[]> = {};
  for (const [key, rows] of Object.entries(raw)) {
    const options: CaseFilesCategoryFilterOption[] = [];
    for (const row of rows ?? []) {
      if (!Array.isArray(row) || row.length < 2) continue;
      const label = String(row[0]).trim();
      const id = String(row[1]).trim();
      if (label && id) options.push({ label, id });
    }
    if (options.length > 0) out[key] = options;
  }
  return out;
}

/**
 * Serializes case-files history-row filters for the list-files API.
 * Backend expects e.g. `Proclaim Folder:A,B;Corres. to:C;Handler:D`
 */
export function serializeCaseFilesHistoryRowFilters(selectedFilters?: Record<string, string[]>): string | undefined {
  if (!selectedFilters || Object.keys(selectedFilters).length === 0) return undefined;

  const rows = [];
  for (const [category, values] of Object.entries(selectedFilters)) {
    if (isCaseFilesCategoryFilterKey(category)) continue;
    const cat = category;
    const cleaned = (values ?? []).filter((v) => v.length > 0);
    if (cat.length === 0 || cleaned.length === 0) continue;
    rows.push({ category: cat, values: cleaned });
  }
  if (rows.length === 0) return undefined;

  rows.sort((a, b) => a.category.localeCompare(b.category));
  return rows.map((r) => `${r.category}:${r.values.join(',')}`).join(';');
}

/** UUIDs for the list-files `categoryFilters` param (Category + Sub-Category selections). */
export function collectCaseFilesCategoryFilterUuids(selectedFilters?: Record<string, string[]>): string[] {
  if (!selectedFilters) return [];
  const ids: string[] = [];
  for (const key of CASE_FILES_CATEGORY_FILTER_KEYS) {
    for (const v of selectedFilters[key] ?? []) {
      const t = v.trim();
      if (t.length > 0) ids.push(t);
    }
  }
  return ids;
}
